import { Suspense, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { useLiveDonationTotals } from '@/routes/-components/use-live-donation-totals';
import { RollingFigure } from '../rolling-figure';
import { Expand, ExpandGroup } from '../expand';
import { SwipeRow, DRIFT_PX_PER_MS } from './swipe-row';
import {
	CoinsTile,
	FundedProjectsTile,
	MicrodonationsTile,
	PartnerNgosTile,
	PhotoTile,
	TotalRaisedTile,
} from './stat-tiles';

const MOBILE_STAT = 'h-44 w-[17rem] shrink-0 snap-start';
const MOBILE_PHOTO = 'h-44 w-52 shrink-0 snap-start';

/**
 * `alwaysGroup`: these counters must read as thousands, and Spanish does not group 4 digits by
 * default (1303 → "1303"). The two big figures are shown in full, with no leading "+": the comms
 * team wants the real, live number rather than an abbreviation of it.
 */
const GROUPED = { alwaysGroup: true };

/**
 * The money figure carries its CENTS, and the count does not — it is a quantity of donations, so a
 * fractional one would be nonsense.
 *
 * This reverses TEC-3832's "rounded to the euro", asked for after seeing it live: a total that moves
 * only when a whole euro lands sits still for most of a minute, and the point of the tile is that it
 * is alive. `www` has always shown two, so the two sites now agree.
 *
 * BOTH the live figure and the Suspense fallback below use this. They must, or the tile would reformat
 * the moment the window query lands — the fallback is deliberately the same figure the live counter
 * starts from, so that nothing moves when it arrives.
 */
const MONEY_DIGITS = 2;
const COUNT_DIGITS = 0;

/**
 * Hero stats bento: the four real global stats (`getWorldcooGlobalStats`, prefetched on the route)
 * mixed with photos, formatted for the current locale.
 *
 * The two headline figures are LIVE — see `LiveFigures`. Everything about the arrangement is in
 * `StatsBody`, which both the live and the static rendering share, so the fallback and the real thing
 * cannot lay out differently.
 */
export function Stats() {
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	// Not rounded: the fallback and the live counter have to render the same string for the same figure,
	// and `Math.round` here would have shown a different total for the moment before the window landed.
	const totalRaised = `${formatNumber(language, stats.totalRaised.amount, MONEY_DIGITS, GROUPED)} €`;
	const microDonations = formatNumber(language, stats.totalDonations, COUNT_DIGITS, GROUPED);

	return (
		// The boundary covers the FIRST load only. Its fallback is the whole bento with the figures the
		// global-stats query already carries — not a blank box — so nothing moves when the window query
		// lands and the two counters simply start climbing. It cannot flash again later: the live query's
		// key is stable, so a window rollover is a refetch, not a new key, and a refetch never returns to
		// `isPending`. See `use-live-donation-totals.ts`.
		<Suspense fallback={<StatsBody raised={totalRaised} microdonations={microDonations} />}>
			<LiveFigures />
		</Suspense>
	);
}

/**
 * The same two tiles, counting — as ODOMETERS: `RollingFigure` rolls its 0–9 columns from a zeroed
 * figure on mount, and afterwards rolls only the digits a donation actually moved.
 *
 * Both figures come from ONE `useLiveDonationTotals` call: the amount and the count are computed in the
 * same pass over the same donations, which is what makes "one donation adds its amount and one unit"
 * true rather than merely likely.
 */
function LiveFigures() {
	const { language } = useTranslation();
	const { raisedEuros, donationCount } = useLiveDonationTotals();

	return (
		<StatsBody
			raised={<RollingFigure locale={language} value={raisedEuros} suffix="€" decimalDigits={MONEY_DIGITS} />}
			microdonations={<RollingFigure locale={language} value={donationCount} decimalDigits={COUNT_DIGITS} />}
		/>
	);
}

/**
 * Two arrangements of the same tiles. Desktop is the three-column grid with the photos bleeding off
 * the left and right edges. Mobile is TWO horizontally swipeable rows, per the mobile hand-off — not
 * a single stacked column, which is what this used to do: stacked, the four figures pushed the rest of
 * the page down by nearly a screen and a half.
 *
 * The two supporting figures are read here rather than passed in: they are static, and the global-stats
 * query they come from is already resolved by the time either caller renders.
 */
function StatsBody(props: { raised: ReactNode; microdonations: ReactNode }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	const projectsFunded = formatNumber(language, stats.projectsFunded, 0, GROUPED);
	const partnerNgos = formatNumber(language, stats.socialOrganizations, 0, GROUPED);
	const totalRaised = props.raised;
	const microDonations = props.microdonations;

	const kids = { src: '/redesign/hero-kids.jpg', alt: t`Children we help` };
	const girl = { src: '/redesign/hero-girl.jpg', alt: t`A child smiling` };

	return (
		// The whole bento is ONE trigger rather than a trigger per box: the sweep's point is that the
		// boxes arrive in relation to each other, and observing each of them separately would make the
		// order a function of the reader's scroll speed instead of the design. `--rise-i` on each box is
		// that order — left to right across the bento, a column's lower box trailing the one above it.
		// Left to right across the bento — one step per COLUMN, and a column's lower box half a step
		// behind the one above it, so a stacked pair still reads as a pair. See `Expand`.
		<ExpandGroup asChild>
			<div className="relative pb-16 md:pb-24">
				{/* Anchored to the BENTO, not the viewport, so the 24px gap to the grid holds at every width
			    (600px half-width + 24). The card keeps its designed 220x384 size and simply RUNS OFF the
			    screen: the section clips it, which is what gives the design's half-cropped photos. Sizing
			    them to the leftover space instead — what this used to do — made them a 96px sliver at 1440
			    and a squat slab on an ultrawide, neither of which is the design. Shown from 1280px up,
			    below which the sliver left over is too thin to read as a photo. The 120px drop is the design's:
			    the photos hang below the top of the staggered columns rather than starting level with them. */}
				<Expand index={0} asChild>
					<PhotoTile
						{...kids}
						className="absolute top-30 right-[calc(50%_+_624px)] hidden h-96 w-[13.75rem] xl:block"
					/>
				</Expand>
				<Expand index={4} asChild>
					<PhotoTile {...girl} className="absolute top-30 left-[calc(50%_+_624px)] hidden h-96 w-[13.75rem] xl:block" />
				</Expand>

				{/* Each row LEADS with one of the two headline figures, so both are readable before anyone
			    swipes; what follows is the supporting metric and then the decorative tile. The kids photo
			    is desktop-only — on mobile it would be a second child portrait next to the girl, and the
			    mobile hand-off only shows one.
			    Slightly different speeds so the rows do not travel in lockstep, which reads as one wide
			    block sliding and hides that they are two separate rows you can swipe. */}
				<div className="flex flex-col gap-4 md:hidden">
					{/* The second row is offset half a beat, so the two rows arrive interleaved rather than in
				    two salvos — the same reason they drift at different speeds. */}
					<SwipeRow speed={DRIFT_PX_PER_MS}>
						<Expand index={0} asChild>
							<TotalRaisedTile value={totalRaised} className={MOBILE_STAT} />
						</Expand>
						<Expand index={1} asChild>
							<FundedProjectsTile value={projectsFunded} className={MOBILE_STAT} />
						</Expand>
						<Expand index={2} asChild>
							<PhotoTile {...girl} className={MOBILE_PHOTO} />
						</Expand>
					</SwipeRow>
					<SwipeRow speed={DRIFT_PX_PER_MS * 1.35}>
						<Expand index={0.5} asChild>
							<MicrodonationsTile value={microDonations} className={MOBILE_STAT} />
						</Expand>
						<Expand index={1.5} asChild>
							<PartnerNgosTile value={partnerNgos} className={MOBILE_STAT} />
						</Expand>
						<Expand index={2.5} asChild>
							<CoinsTile className={MOBILE_STAT} />
						</Expand>
					</SwipeRow>
				</div>

				{/* The three columns are STAGGERED, not aligned: in the hand-off the microdonations column starts
			    highest and runs lowest, the projects column sits a little below it, and the photo lower still,
			    so the bento reads as a loose arrangement rather than a table. That means each column owns its
			    own tile heights and its own top offset — `items-start` is what stops the grid from stretching
			    them all back to a common height, which is what made the tiles line up in a row. */}
				<div className="mx-auto hidden max-w-[1200px] grid-cols-3 items-start gap-4 px-6 md:grid xl:px-0">
					<div className="mt-10 flex flex-col gap-4">
						<Expand index={1} asChild>
							<FundedProjectsTile value={projectsFunded} className="h-64" />
						</Expand>
						<Expand index={1.5} asChild>
							<CoinsTile className="h-76" />
						</Expand>
					</div>
					<Expand index={2} asChild>
						<TotalRaisedTile value={totalRaised} className="mt-20 h-136" />
					</Expand>
					<div className="flex flex-col gap-4">
						<Expand index={3} asChild>
							<MicrodonationsTile value={microDonations} className="h-96" />
						</Expand>
						<Expand index={3.5} asChild>
							<PartnerNgosTile value={partnerNgos} className="h-72" />
						</Expand>
					</div>
				</div>
			</div>
		</ExpandGroup>
	);
}
